import React from "react";

import ListingFieldGroup from "../components/ListingFieldGroup";
import ListingLanguageBlock from "../components/ListingLanguageBlock";
import {
  fieldHint,
  fieldInput,
  fieldLabel,
  grid4Wide,
} from "../listingEditorStyles";

/**
 * "Ausstattung": the rent figures and the two amenity fields.
 *
 * The amenity inputs are unchanged in substance. They are two
 * COMMA-separated strings that `buildAmenities` splits on "," and pairs by
 * position into `{ label_de, label_en }`. This section only makes that rule
 * visible — it introduces no array state, no chips and no repeater, because
 * any of those would need a different payload builder.
 */

/**
 * How many amenities a field currently holds.
 *
 * Mirrors `buildAmenities` in `utils/listingPayload.js` exactly — same split,
 * same trim, same empty filter. If the two ever drift, the counter would lie
 * about what a save sends, so a test pins this count against the real payload.
 */
function countEntries(value) {
  return (value || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean).length;
}

const entryLabel = (n) => `${n} ${n === 1 ? "Eintrag" : "Einträge"}`;

export default function ListingAmenitiesSection({
  form,
  onChange,
  amenitiesDe,
  amenitiesEn,
  setAmenitiesDe,
  setAmenitiesEn,
}) {
  const countDe = countEntries(amenitiesDe);
  const countEn = countEntries(amenitiesEn);
  const uneven = countDe > 0 && countEn > 0 && countDe !== countEn;

  return (
    <>
      <ListingFieldGroup
        title="Miet- & Objektdaten"
        description="Preis und Eckdaten, wie sie auf der Website erscheinen."
      >
        <div className={grid4Wide}>
          <div>
            <label htmlFor="listing-price-chf-month" className={fieldLabel}>
              Preis / Monat
            </label>
            <div className="relative">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground"
              >
                CHF
              </span>
              {/* The adornment is decoration only — "CHF" never enters the
                  field value, which stays the plain number the payload
                  parses with parseInt. */}
              <input
                id="listing-price-chf-month"
                type="number"
                name="price_chf_month"
                value={form.price_chf_month}
                onChange={onChange}
                min={0}
                className={`${fieldInput} pl-12`}
              />
            </div>
          </div>
          <div>
            <label htmlFor="listing-bedrooms" className={fieldLabel}>
              Schlafzimmer
            </label>
            <input
              id="listing-bedrooms"
              type="number"
              name="bedrooms"
              value={form.bedrooms}
              onChange={onChange}
              min={0}
              className={fieldInput}
            />
          </div>
          <div>
            <label htmlFor="listing-bathrooms" className={fieldLabel}>
              Badezimmer
            </label>
            <input
              id="listing-bathrooms"
              type="number"
              name="bathrooms"
              value={form.bathrooms}
              onChange={onChange}
              min={0}
              className={fieldInput}
            />
          </div>
          <div>
            <label htmlFor="listing-size-sqm" className={fieldLabel}>
              Fläche (m²)
            </label>
            <input
              id="listing-size-sqm"
              type="number"
              name="size_sqm"
              value={form.size_sqm}
              onChange={onChange}
              min={0}
              className={fieldInput}
            />
          </div>
        </div>
      </ListingFieldGroup>

      <ListingFieldGroup
        title="Ausstattungsmerkmale"
        description="Mehrere Einträge mit Komma trennen. Der erste deutsche Eintrag gehört zum ersten englischen, der zweite zum zweiten und so weiter."
      >
        {/* Two columns only from `lg`: below that the editor content is too
            narrow for two comma-separated lists side by side. */}
        <div className="grid gap-5 lg:grid-cols-2">
          <ListingLanguageBlock title="Deutsch" code="DE">
            <div>
              <label htmlFor="listing-amenities-de" className={fieldLabel}>
                Ausstattung
              </label>
              <input
                id="listing-amenities-de"
                type="text"
                value={amenitiesDe}
                onChange={(e) => setAmenitiesDe(e.target.value)}
                placeholder="z.B. WLAN, Waschmaschine, Balkon"
                className={fieldInput}
              />
              <p className={fieldHint}>{entryLabel(countDe)}</p>
            </div>
          </ListingLanguageBlock>

          <ListingLanguageBlock title="English" code="EN">
            <div>
              <label htmlFor="listing-amenities-en" className={fieldLabel}>
                Amenities
              </label>
              <input
                id="listing-amenities-en"
                type="text"
                value={amenitiesEn}
                onChange={(e) => setAmenitiesEn(e.target.value)}
                placeholder="e.g. WiFi, Washing machine, Balcony"
                className={fieldInput}
              />
              <p className={fieldHint}>{entryLabel(countEn)}</p>
            </div>
          </ListingLanguageBlock>
        </div>

        {uneven ? (
          <p className="m-0 text-[13px] text-muted-foreground">
            Ungleich viele Einträge. Für die überzähligen wird die fehlende
            Sprache mit dem vorhandenen Wert gefüllt.
          </p>
        ) : null}
      </ListingFieldGroup>
    </>
  );
}
